import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, map } from 'rxjs';
import { EventBusService } from './event-bus.service';
import { AppEventType } from '../enums/app-events.enum';
import { NotificationsService } from './notifications.service';
import { LOCAL_NOTIFICATION_ACTION_TYPE } from '../enums/notification/notification.enum';
import { UserPositionResult } from '../models/log-user-position/log-user-position-response-interface';
import { LogService } from '../baseServices/logging/log.service';

export type ProjectBoundaryStatus = 'in-bounds' | 'out-of-bounds' | 'unknown';

@Injectable({
  providedIn: 'root'
})
export class ProjectBoundaryTrackingService implements OnDestroy{

  private trackUserPositionSubscription: Subscription;

  private readonly boundaryStatusSubject = new BehaviorSubject<ProjectBoundaryStatus>('unknown');
  readonly boundaryStatus$ = this.boundaryStatusSubject.asObservable();

  private OUT_OF_BOUNDS_NOTIFICATION = {
    id: LOCAL_NOTIFICATION_ACTION_TYPE.OUT_OF_BOUNDS,
    title: 'Out of Bounds',
    body: 'Your last reported position was not within the project boundary. Please visit the project map to determine the boundary.'
  };

  constructor(
    private eventBusService: EventBusService,
    private notificationsService: NotificationsService,
    private logService: LogService
  ) {
    this.initSubscriptions();
  }

  initSubscriptions(): void {
    this.trackUserPositionSubscription = this.eventBusService.on(AppEventType.USER_CURRENT_POSITION_LOGGED, (logUserPositionResponse) => {
        const isInProjectBoundaryValue = logUserPositionResponse?.Result?.IsInProjectBoundary;
        if (logUserPositionResponse?.ContextEnded || logUserPositionResponse?.Code !== 200) {
          this.setProjectBoundaryStatus('unknown');
        } else if (isInProjectBoundaryValue === UserPositionResult.InBounds || isInProjectBoundaryValue === UserPositionResult.OutOfBounds) {
          this.setProjectBoundaryStatus(isInProjectBoundaryValue === UserPositionResult.InBounds ? 'in-bounds' : 'out-of-bounds');
        } else {
          this.setProjectBoundaryStatus('unknown');
        }
      });
  }

  ngOnDestroy(): void {
    this.trackUserPositionSubscription.unsubscribe();
  }


  private setProjectBoundaryStatus(status: ProjectBoundaryStatus): void {
    const previous = this.boundaryStatusSubject.value;
    if (previous !== status) {
      if (status === 'in-bounds') {
        this.logService.event('ProjectBoundary.Entered', { previous, current: status });
      } else if (status === 'out-of-bounds') {
        this.logService.event('ProjectBoundary.Exited', { previous, current: status });
      }
    }
    this.boundaryStatusSubject.next(status);
  }

  public get isInProjectBoundary$(): Observable<boolean | null> {
    return this.boundaryStatus$.pipe(map(status => status === 'unknown' ? null : status === 'in-bounds'));
  }

  /**
     * Schedules a local notification to alert the user
     * that they are outside the project boundary.
     * 
     * Appears in the device's notification tray.
     * Does not require internet connection.
   */
  public scheduleOutOfBoundaryNotification(){
    this.notificationsService.scheduleLocalNotification(this.OUT_OF_BOUNDS_NOTIFICATION);
  }


  /**
     * removes a notification that has already been delivered 
     * and is currently visible in the device’s notification tray.
   */
  public removeScheduledOutOfBoundaryNotification(){
    this.notificationsService.removeDeliveredLocalNotification(this.OUT_OF_BOUNDS_NOTIFICATION);
  }
}
